import Toggle from 'components/common/Toggle'
import { useModalContext } from 'context/ModalContext'
import { useCallback, useState } from 'react'
import BaseModal from './BaseModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from 'components/Button'
import { EventAsset, useEditEventAssetMutation } from 'graphql/generated'
import { faPhotoVideo } from '@fortawesome/pro-duotone-svg-icons'
import { useControlledInput, useQueryErrorHandler } from 'hooks'

interface IProps {
  asset: Omit<EventAsset, 'event'>
}

export function useEditEventAssetModal() {
  const { setModalContent } = useModalContext()
  return useCallback(
    (props: IProps) => {
      setModalContent(<EditEventAssetModal {...props} />)
    },
    [setModalContent]
  )
}

export function EditEventAssetModal({ asset }: IProps) {
  const { hideModal } = useModalContext()
  const [caption, setCaption] = useControlledInput(asset.caption)
  const [_public, setPublic] = useState(asset.public)
  const onCancel = () => {
    hideModal()
  }

  const onError = useQueryErrorHandler('Failed to save asset')
  const [save] = useEditEventAssetMutation({
    variables: {
      assetId: asset.id,
      public: _public,
      caption: caption
    },
    onError,
    onCompleted: () => hideModal()
  })

  return (
    <BaseModal>
      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
            <FontAwesomeIcon
              icon={faPhotoVideo}
              className={'h-6 w-6 text-green-600'}
            />
          </div>

          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3
              className="text-lg leading-6 font-medium text-gray-900"
              id="modal-headline"
            >
              {`Edit Asset \u00b7 ${asset.caption}`}
            </h3>

            <div className="mt-6">
              <div>
                <label htmlFor="email-address">Caption</label>
                <input
                  required
                  autoFocus
                  type="email"
                  autoComplete="email"
                  className="appearance-none relative block w-full px-3 border font-thin border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={caption}
                  onChange={setCaption}
                />
              </div>

              <Toggle
                label={`Should this asset be visible?`}
                checked={_public}
                onChange={setPublic}
                labelClassName={'text-base font-normal'}
                className={'pb-4 pt-4'}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 py-3 px-6 flex flex-row-reverse">
        <Button onClick={() => save()} label={'Save'} />
        <Button
          onClick={onCancel}
          color={'white'}
          label={'Cancel'}
          className={'mr-3'}
        />
      </div>
    </BaseModal>
  )
}
